import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Zap,
  AlertTriangle,
  ChevronDown,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Clock,
  SkipForward,
  Ban,
  Circle,
} from 'lucide-react';
import { safeFetch } from '../../../lib/api';

export interface AssignedRunStep {
  id: string;
  title: string;
  actionType: string | null;
  status: string;
  scheduledAt: string | null;
  executedAt: string | null;
  completedAt: string | null;
  error: string | null;
  attempts: number | null;
}

export interface AssignedAutomationRun {
  id: string;
  runId: string | null;
  automationName: string;
  automationVersion: number | null;
  status: string;
  initiatedByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  nextScheduledStepAt: string | null;
  errorSummary: string | null;
  steps: AssignedRunStep[];
}

interface DrawerAssignedWorkflowsProps {
  uid: string;
  /** Bumped by the drawer on every (re)load; refetch when it changes. */
  refreshSignal?: number;
}

const ACTIVE_RUN_STATUSES = new Set(['pending', 'running', 'paused']);

const STATUS_CHIP: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  running: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  paused: 'bg-slate-100 text-slate-600 border-slate-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  completed_with_errors: 'bg-amber-50 text-amber-800 border-amber-200',
  skipped: 'bg-slate-100 text-slate-500 border-slate-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STEP_ICON: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 className="h-3.5 w-3.5" />,
  failed: <XCircle className="h-3.5 w-3.5" />,
  running: <PlayCircle className="h-3.5 w-3.5" />,
  scheduled: <Clock className="h-3.5 w-3.5" />,
  skipped: <SkipForward className="h-3.5 w-3.5" />,
  cancelled: <Ban className="h-3.5 w-3.5" />,
  pending: <Circle className="h-3.5 w-3.5" />,
};

function statusLabel(status: string): string {
  const spaced = String(status || '').replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '—';
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function timeOr(v: string | null | undefined): string {
  return fmtDateTime(v) || '—';
}

/** The one time that matters for a step, given where it is in its life. */
function stepTimeLine(step: AssignedRunStep): string {
  switch (step.status) {
    case 'completed':
      return `Completed ${timeOr(step.completedAt)}`;
    case 'scheduled':
      return `Scheduled for ${timeOr(step.scheduledAt)}`;
    case 'running':
      return `Started ${timeOr(step.executedAt)}`;
    case 'failed':
      return `Failed ${timeOr(step.completedAt || step.executedAt)}`;
    case 'skipped':
      return `Skipped ${timeOr(step.completedAt)}`;
    case 'cancelled':
      return `Cancelled ${timeOr(step.completedAt)}`;
    default:
      return '—';
  }
}

/**
 * Assigned automations for THIS applicant, inside the drawer.
 *
 * Display only: every automation run assigned to the applicant as a
 * card — name, run status, who started it and when — with an
 * expandable step timeline showing each step's status and its time
 * (completion time for done steps, due time for scheduled ones, the
 * error plainly for failed ones). Data is the `runs` projection on
 * the SAME /activity response the records feed consumes (assembled
 * server-side from the run docs — no parallel store), so this section
 * refreshes on the drawer's existing cycle: no new fetching machinery.
 * Step controls live in the run itself, not here.
 */
export const DrawerAssignedWorkflows: React.FC<DrawerAssignedWorkflowsProps> = ({ uid, refreshSignal }) => {
  const [runs, setRuns] = useState<AssignedAutomationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/activity`);
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load assigned automations');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    setExpanded({});
    fetchRuns();
  }, [fetchRuns]);

  const lastSignalRef = useRef(refreshSignal ?? 0);
  useEffect(() => {
    const s = refreshSignal ?? 0;
    if (s !== lastSignalRef.current) {
      lastSignalRef.current = s;
      fetchRuns();
    }
  }, [refreshSignal, fetchRuns]);

  const isExpanded = (run: AssignedAutomationRun) =>
    expanded[run.id] ?? ACTIVE_RUN_STATUSES.has(run.status);

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <Zap className="h-4 w-4 text-violet-600" />
        <h3 className="text-sm font-extrabold text-slate-900 tracking-tight">
          Assigned automations{' '}
          {runs.length > 0 && (
            <span className="text-slate-400 font-bold tabular-nums">({runs.length})</span>
          )}
        </h3>
      </div>

      {loading && runs.length === 0 ? (
        <div className="p-4 space-y-2" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse" />
          ))}
          <span className="sr-only">Loading assigned automations…</span>
        </div>
      ) : error ? (
        <div className="px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-xs font-bold text-slate-600 flex-1">{error}</p>
          <button type="button" onClick={fetchRuns} className="text-xs font-bold text-indigo-600 underline cursor-pointer">
            Retry
          </button>
        </div>
      ) : runs.length === 0 ? (
        <p className="px-4 py-4 text-xs font-semibold text-slate-500">
          No automations assigned to this applicant yet.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {runs.map((run) => {
            const done = run.steps.filter((s) => s.status === 'completed').length;
            const open = isExpanded(run);
            return (
              <li key={run.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <p className="min-w-0 flex-1 font-extrabold text-[13px] text-slate-900 break-words">
                    {run.automationName}
                  </p>
                  {run.automationVersion != null && (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 shrink-0">
                      v{run.automationVersion}
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 border shrink-0 ${STATUS_CHIP[run.status] || STATUS_CHIP.pending}`}
                  >
                    {statusLabel(run.status)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {run.runId ? `${run.runId} · ` : ''}
                  Started {timeOr(run.createdAt)}
                  {run.initiatedByName ? ` · by ${run.initiatedByName}` : ''}
                </p>
                {ACTIVE_RUN_STATUSES.has(run.status) && run.nextScheduledStepAt && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Next step due {fmtDateTime(run.nextScheduledStepAt)}
                  </p>
                )}
                {run.steps.length > 0 && (
                  <p className="text-[11px] font-semibold text-slate-600 mt-0.5">
                    {done}/{run.steps.length} steps completed
                  </p>
                )}
                {run.errorSummary && (
                  <p className="mt-2 rounded-lg bg-red-50 border border-red-200/70 px-2.5 py-1.5 text-[11px] text-red-700 whitespace-pre-line">
                    {run.errorSummary}
                  </p>
                )}
                {run.steps.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setExpanded((prev) => ({ ...prev, [run.id]: !open }))}
                      aria-expanded={open}
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-700 cursor-pointer"
                    >
                      Steps ({run.steps.length})
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
                    </button>
                    {open && (
                      <ol className="mt-2 space-y-2">
                        {run.steps.map((step) => (
                          <li key={step.id} className="flex items-start gap-2.5 text-xs">
                            <span
                              className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${STATUS_CHIP[step.status] || STATUS_CHIP.pending}`}
                              aria-hidden="true"
                            >
                              {STEP_ICON[step.status] || STEP_ICON.pending}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <p className="font-bold text-slate-800 leading-snug break-words">{step.title}</p>
                                <span
                                  className={`text-[10px] font-extrabold uppercase tracking-wide rounded-full px-1.5 py-0.5 border shrink-0 ${STATUS_CHIP[step.status] || STATUS_CHIP.pending}`}
                                >
                                  {statusLabel(step.status)}
                                </span>
                              </div>
                              <p className="text-slate-400 mt-0.5">
                                {stepTimeLine(step)}
                                {step.attempts != null && step.attempts > 1 ? ` · ${step.attempts} attempts` : ''}
                              </p>
                              {step.error && (
                                <p className="mt-1 rounded-lg bg-red-50 border border-red-200/70 px-2.5 py-1.5 text-[11px] text-red-700 whitespace-pre-line">
                                  {step.error}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
